import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import DashboardLayout from "../components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, ResponsiveContainer, Legend, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell } from "recharts";
import { ChevronLeft, ClipboardList, TrendingDown, TrendingUp, Minus, Send, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { API } from "../App";

const PHASE_LABELS = { pre: "Pre-psychoeducation", post: "Post-psychoeducation" };

const SubscaleBar = ({ label, score, min, max, percent, color }) => (
  <div className="flex items-center gap-3">
    <span className="text-xs text-muted-foreground w-[160px] truncate" title={label}>{label}</span>
    <div className="flex-1 bg-muted rounded-full h-2.5">
      <div className="h-2.5 rounded-full transition-all" style={{ width: `${percent}%`, backgroundColor: color || "hsl(var(--primary))" }} />
    </div>
    <span className="text-xs font-medium w-[70px] text-right">{score}/{max} ({percent}%)</span>
  </div>
);

export default function DersResultsPage({ user, logout }) {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [assigning, setAssigning] = useState(false);

  const fetchData = async () => {
    try {
      const [resultsRes, assignRes] = await Promise.all([
        axios.get(`${API}/ders/patient/${patientId}`, { withCredentials: true }),
        axios.get(`${API}/ders/assignments/${patientId}`, { withCredentials: true }),
      ]);
      setData(resultsRes.data);
      setAssignments(assignRes.data);
    } catch (e) {
      console.error(e);
      if (e.response?.status === 404) {
        toast.error("Patient non trouve");
        navigate(-1);
      }
    }
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, [patientId]);

  const handleAssign = async (phase) => {
    setAssigning(true);
    try {
      await axios.post(`${API}/ders/assign/${patientId}`, { phase }, { withCredentials: true });
      toast.success(`DERS (${PHASE_LABELS[phase]}) assigne avec succes`);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de l'assignation");
    }
    setAssigning(false);
  };

  if (loading) {
    return (
      <DashboardLayout user={user} logout={logout}>
        <div className="flex items-center justify-center h-40">
          <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" />
        </div>
      </DashboardLayout>
    );
  }

  const entries = data?.entries || [];
  const comparison = data?.comparison;
  const hasPending = assignments.some(a => a.status === "pending");
  const pendingPre = assignments.some(a => a.status === "pending" && a.phase === "pre");
  const pendingPost = assignments.some(a => a.status === "pending" && a.phase === "post");

  // Prepare radar chart data from comparison
  const radarData = comparison ? Object.entries(comparison.subscales).map(([key, sub]) => ({
    subject: sub.label,
    pre: sub.pre,
    post: sub.post,
  })) : [];

  // Subscale colors
  const subscaleColors = ["#6366f1", "#8b5cf6", "#22d3ee", "#10b981", "#f59e0b", "#ef4444"];

  return (
    <DashboardLayout user={user} logout={logout}>
      <div className="max-w-5xl mx-auto space-y-6 pb-12" data-testid="ders-results-page">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="mb-1" data-testid="ders-results-back">
              <ChevronLeft className="h-4 w-4 mr-1" /> Retour
            </Button>
            <h1 className="text-2xl font-bold text-foreground">
              DERS — {data?.patient_name || "Patient"}
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Echelle des Difficultes de Regulation Emotionnelle
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => handleAssign("pre")}
              disabled={assigning || pendingPre}
              data-testid="assign-ders-pre"
            >
              <Send className="h-3.5 w-3.5 mr-1.5" />
              {pendingPre ? "Pre en attente" : "Assigner Pre"}
            </Button>
            <Button
              size="sm"
              onClick={() => handleAssign("post")}
              disabled={assigning || pendingPost}
              data-testid="assign-ders-post"
            >
              <Send className="h-3.5 w-3.5 mr-1.5" />
              {pendingPost ? "Post en attente" : "Assigner Post"}
            </Button>
          </div>
        </div>

        {/* No data */}
        {entries.length === 0 && (
          <Card className="border-border/40">
            <CardContent className="p-8 text-center">
              <ClipboardList className="h-10 w-10 text-muted-foreground/40 mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">
                Aucune evaluation DERS pour ce patient.
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Assignez un questionnaire Pre-psychoeducation pour commencer.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Comparison Card (pre vs post) */}
        {comparison && (
          <Card className="border-border/40" data-testid="ders-comparison-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <ClipboardList className="h-4 w-4 text-primary" />
                Comparaison Pre / Post
              </CardTitle>
              <p className="text-[10px] text-muted-foreground">
                Pre: {new Date(comparison.pre_date).toLocaleDateString("fr-FR")} — Post: {new Date(comparison.post_date).toLocaleDateString("fr-FR")}
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Total score comparison */}
              <div className="grid grid-cols-3 gap-4 text-center">
                <div className="p-3 rounded-lg bg-muted/50">
                  <p className="text-xs text-muted-foreground">Score Pre</p>
                  <p className="text-2xl font-bold text-foreground">{comparison.pre_total}</p>
                  <p className="text-[10px] text-muted-foreground">/180</p>
                </div>
                <div className="p-3 rounded-lg bg-muted/50 flex flex-col items-center justify-center">
                  <p className="text-xs text-muted-foreground">Delta</p>
                  <div className="flex items-center gap-1">
                    {comparison.delta_total > 0 ? (
                      <TrendingDown className="h-5 w-5 text-green-500" />
                    ) : comparison.delta_total < 0 ? (
                      <TrendingUp className="h-5 w-5 text-red-500" />
                    ) : (
                      <Minus className="h-5 w-5 text-muted-foreground" />
                    )}
                    <p className={`text-2xl font-bold ${comparison.delta_total > 0 ? "text-green-600" : comparison.delta_total < 0 ? "text-red-600" : "text-muted-foreground"}`}>
                      {comparison.delta_total > 0 ? "-" : "+"}{Math.abs(comparison.delta_total)}
                    </p>
                  </div>
                  <p className="text-[10px] text-muted-foreground">
                    {comparison.delta_total > 0 ? "Amelioration" : comparison.delta_total < 0 ? "Deterioration" : "Stable"}
                  </p>
                </div>
                <div className="p-3 rounded-lg bg-muted/50">
                  <p className="text-xs text-muted-foreground">Score Post</p>
                  <p className="text-2xl font-bold text-foreground">{comparison.post_total}</p>
                  <p className="text-[10px] text-muted-foreground">/180</p>
                </div>
              </div>

              {/* Subscale comparison table */}
              <div className="space-y-2">
                <p className="text-xs font-medium text-muted-foreground">Sous-echelles</p>
                {Object.entries(comparison.subscales).map(([key, sub], i) => (
                  <div key={key} className="flex items-center justify-between py-1.5 border-b border-border/20 last:border-0">
                    <span className="text-xs text-foreground w-[160px]">{sub.label}</span>
                    <div className="flex items-center gap-4">
                      <span className="text-xs text-muted-foreground w-[40px] text-right">{sub.pre}</span>
                      <div className="flex items-center gap-1 w-[60px] justify-center">
                        {sub.improved ? (
                          <Badge variant="default" className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 text-[10px]">
                            -{sub.delta}
                          </Badge>
                        ) : sub.delta < 0 ? (
                          <Badge variant="default" className="bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 text-[10px]">
                            +{Math.abs(sub.delta)}
                          </Badge>
                        ) : (
                          <Badge variant="secondary" className="text-[10px]">=</Badge>
                        )}
                      </div>
                      <span className="text-xs text-muted-foreground w-[40px] text-right">{sub.post}</span>
                    </div>
                  </div>
                ))}
              </div>

              {/* Radar chart */}
              {radarData.length > 0 && (
                <div className="h-[300px] mt-4">
                  <ResponsiveContainer width="100%" height="100%">
                    <RadarChart data={radarData}>
                      <PolarGrid stroke="hsl(var(--border))" />
                      <PolarAngleAxis dataKey="subject" tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} />
                      <PolarRadiusAxis tick={{ fontSize: 9, fill: "hsl(var(--muted-foreground))" }} />
                      <Radar name="Pre" dataKey="pre" stroke="#6366f1" fill="#6366f1" fillOpacity={0.2} strokeWidth={2} />
                      <Radar name="Post" dataKey="post" stroke="#22d3ee" fill="#22d3ee" fillOpacity={0.2} strokeWidth={2} />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                    </RadarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Individual entries */}
        {entries.length > 0 && (
          <div className="space-y-3">
            <h2 className="text-sm font-semibold text-foreground">Evaluations ({entries.length})</h2>
            {entries.map((entry, idx) => (
              <Card key={entry.entry_id} className="border-border/30" data-testid={`ders-entry-${entry.entry_id}`}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Badge variant={entry.phase === "pre" ? "secondary" : "default"} className={entry.phase === "post" ? "bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-400" : ""}>
                        {PHASE_LABELS[entry.phase] || entry.phase}
                      </Badge>
                      <span className="text-xs text-muted-foreground">
                        {new Date(entry.created_at).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}
                      </span>
                    </div>
                    <div className="text-right">
                      <span className="text-lg font-bold">{entry.scoring?.total}</span>
                      <span className="text-xs text-muted-foreground">/180 ({entry.scoring?.total_percent}%)</span>
                    </div>
                  </div>
                  {/* Subscale bars */}
                  <div className="space-y-2">
                    {entry.scoring?.subscales && Object.entries(entry.scoring.subscales).map(([key, sub], i) => (
                      <SubscaleBar key={key} {...sub} color={subscaleColors[i % subscaleColors.length]} />
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Pending assignments */}
        {assignments.filter(a => a.status === "pending").length > 0 && (
          <Card className="border-amber-200/50 bg-amber-50/20 dark:bg-amber-950/10" data-testid="ders-pending-assignments">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <AlertCircle className="h-4 w-4 text-amber-500" />
                Assignations en attente
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0 space-y-2">
              {assignments.filter(a => a.status === "pending").map(a => (
                <div key={a.assignment_id} className="flex items-center justify-between py-1.5 text-sm">
                  <span>{PHASE_LABELS[a.phase]}</span>
                  <span className="text-xs text-muted-foreground">
                    {new Date(a.created_at).toLocaleDateString("fr-FR")}
                  </span>
                </div>
              ))}
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
}
